// Ügyfél detail + edit (full-page, egy oldal). A form szerkeszthető, ha van clients.manage — különben
// view-only (disabled mezők). Mentés: PATCH If-Match/OCC, optimista detail-frissítés; 422 → mező,
// 409 → „valaki módosította" toast + reload, 403 → toast. Törlés: ConfirmDialog → soft-delete → lista.
import { type ClientStatus } from '@atlas/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DangerZone } from '@/components/shared/danger-zone';
import { EmptyState } from '@/components/shared/empty-state';
import { FormPage } from '@/components/shared/form-page';
import { SectionNav, type SectionNavItem } from '@/components/shared/section-nav';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { clientKeys } from '@/features/clients/api';
import { ClientAccountsSection } from '@/features/clients/components/client-accounts-section';
import {
  ClientBillingProfilesSection,
  useClientBillingProfiles,
} from '@/features/clients/components/client-billing-profiles-section';
import { DeactivationBillingNotice } from '@/components/shared/deactivation-billing-notice';
import { ClientForm } from '@/features/clients/components/client-form';
import { serviceAssignmentKeys } from '@/features/service-assignments/api';
import { ClientPaymentCardsSection } from '@/features/clients/components/client-payment-cards-section';
import { ClientServicesSection } from '@/features/clients/components/client-services-section';
import {
  buildClientErrorMap,
  clientFormSchema,
  type ClientFormValues,
} from '@/features/clients/form';
import { CLIENT_SECTION } from '@/features/clients/sections';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

export const Route = createFileRoute('/clients/$id')({
  component: ClientDetailPage,
});

type DetailPage = Awaited<ReturnType<typeof api.clients.get.query>>;

function toFormValues(body: {
  name: string;
  email: string | null;
  phone: string | null;
  status: string;
  autoBilling: boolean;
}): ClientFormValues {
  const status: ClientStatus =
    body.status === 'pending' || body.status === 'inactive' ? body.status : 'active';
  return {
    name: body.name,
    email: body.email ?? '',
    phone: body.phone ?? '',
    status,
    autoBilling: body.autoBilling,
  };
}

function ClientDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canManage = can('clients.manage');
  const canDelete = can('clients.delete');
  // A kapcsolódó szekciók page-gate-jei: a `*.view` jog nélkül a szekció MEG SEM JELENIK (a BFF is 403-at
  // adna, tehát egy megjelenített, de mindig hibázó szekció csak félrevezetne).
  const canViewAccounts = can('accounts.view');
  const canViewServices = can('service_assignments.view');
  const canViewBillingProfiles = can('billing_profiles.view');
  const canViewPaymentCards = can('payment_cards.view');

  const detailKey = clientKeys.detail(id);
  const detailQuery = api.clients.get.useQuery(detailKey, { params: { id } });
  const detail = detailQuery.data?.status === 200 ? detailQuery.data.body : undefined;
  const version = detail?.version;

  const [confirmOpen, setConfirmOpen] = useState(false);

  /**
   * Kapcsolható-e az AUTOMATIKUS SZÁMLÁZÁS? Számla csak ELSŐDLEGES számlázási profilról áll ki, ezért
   * elsődleges profil nélkül bekapcsolni értelmetlen — a generálás vagy hibás vevő-adattal futna, vagy
   * kihagyná az ügyfelet.
   *
   * A LEGACY-VÉDELEM ITT LAKIK: az importált adatban van néhány ügyfél, ahol az `autoBilling` MÁR BE VAN
   * kapcsolva elsődleges profil nélkül (mérve: 15 db a 283-ból). Ha a kapcsolót náluk is
   * zárolnánk, (a) nem tudnák KIKAPCSOLNI a hibás állapotot, és (b) minden mentés visszaírná a rosszat.
   * Ezért CSAK az ÚJ bekapcsolást akadályozzuk: ha már be van kapcsolva, a mező nyitva marad (kikapcsolható),
   * ha ki van kapcsolva és nincs elsődleges profil, akkor zárolt + indoklás.
   */
  // A profil-listát UGYANABBÓL a lekérésből olvassuk, amit a Számlázási profilok szekció is használ
  // (`useClientBillingProfiles`) — így egy ITT most felvett elsődleges profil AZONNAL feloldja a kapcsolót.
  // A detail beágyazott `billingProfiles` tömbje erre nem jó: azt egy profil-írás nem invalidálja.
  const profilesQ = useClientBillingProfiles(id);
  /**
   * A rail elemei — CSAK a ténylegesen renderelt szekciók. A jog-kapuzás (`*.view`) és a törlés-jog miatt
   * a lap szekciói felhasználónként eltérnek; egy fix lista olyan célra mutatna, ami nincs a lapon (a
   * kattintás némán nem csinálna semmit). A sorrend a DOM-sorrendé (`sections.ts`).
   */
  const navItems: SectionNavItem[] = [
    { id: CLIENT_SECTION.data, label: t('clients.sectionData') },
    ...(canViewAccounts
      ? [{ id: CLIENT_SECTION.accounts, label: t('clients.accountsTitle') }]
      : []),
    ...(canViewServices
      ? [{ id: CLIENT_SECTION.services, label: t('clients.servicesTitle') }]
      : []),
    ...(canViewPaymentCards
      ? [{ id: CLIENT_SECTION.paymentCards, label: t('clients.paymentCardsTitle') }]
      : []),
    ...(canViewBillingProfiles
      ? [{ id: CLIENT_SECTION.billingProfiles, label: t('clients.billingProfilesTitle') }]
      : []),
    ...(canDelete ? [{ id: CLIENT_SECTION.danger, label: t('common.dangerZone') }] : []),
  ];

  const hasPrimaryBillingProfile =
    profilesQ.data?.status === 200 && profilesQ.data.body.profiles.some((p) => p.isPrimary);
  const autoBillingBlocked: 'noPrimaryProfile' | undefined =
    !hasPrimaryBillingProfile && detail?.autoBilling !== true ? 'noPrimaryProfile' : undefined;

  const form = useForm<ClientFormValues>({
    // Lásd clients.new.tsx: a cast a schema-output (nullable/optional) → string-alapú form-értékek híd.
    resolver: zodResolver(clientFormSchema, {
      errorMap: buildClientErrorMap(t),
    }) as Resolver<ClientFormValues>,
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: { name: '', email: '', phone: '', status: 'active', autoBilling: false },
  });

  // A betöltött detail → form-alapértékek (csak amíg a felhasználó nem piszkálta a formot).
  const { reset } = form;
  const isDirty = form.formState.isDirty;
  useEffect(() => {
    // CSAK amíg a felhasználó nem piszkálta a formot — egy háttér-refetch ne írja felül a szerkesztést.
    if (detail && !isDirty) reset(toFormValues(detail));
  }, [detail, isDirty, reset]);

  /**
   * A tulajdonos hozzárendelései — CSAK az inaktiválás-figyelmeztetéshez (hány nyitott számlázás-sor zárul
   * le, és abból hány utólag fizetett). UGYANAZ a query-kulcs, amit a „Szolgáltatások" szekció használ,
   * tehát a TanStack Query összevonja: nincs plusz hálózati kör.
   */
  const assignmentsQuery = api.serviceAssignments.useQuery(
    [...serviceAssignmentKeys.lists(), 'by-client', id],
    { query: { clientId: id, limit: 200 } },
  );
  const assignmentsForNotice =
    assignmentsQuery.data?.status === 200 ? assignmentsQuery.data.body.assignments : [];

  const updateMutation = useEntityMutation<ClientFormValues, DetailPage>({
    mutationFn: (values) =>
      api.clients.update.mutation({
        params: { id },
        body: values,
        headers: { 'if-match': version !== undefined ? String(version) : undefined },
      }),
    invalidateKeys: [detailKey, clientKeys.lists()],
    optimistic: {
      queryKey: detailKey,
      update: (old) => {
        if (old?.status !== 200) return old;
        return {
          ...old,
          body: {
            ...old.body,
            name: form.getValues('name'),
            email: form.getValues('email') || null,
            phone: form.getValues('phone') || null,
            status: form.getValues('status'),
            autoBilling: form.getValues('autoBilling'),
          },
        };
      },
    },
    onFieldErrors: (field, messages) =>
      form.setError(field as keyof ClientFormValues, { message: messages.join(', ') }),
    onConflict: () => void detailQuery.refetch(),
    messages: {
      success: t('toast:clients.saved'),
      conflict: t('toast:clients.conflict'),
      forbidden: t('toast:clients.forbidden'),
      validation: t('toast:clients.validation'),
      error: t('toast:clients.error'),
      // Az ÍRÁS sikerülhetett, csak a visszaolvasás bukott átmenetileg (503) — a teendő az újratöltés.
      unavailable: t('toast:clients.unavailable'),
    },
  });

  const removeMutation = useEntityMutation<{ id: string }, unknown>({
    mutationFn: ({ id: clientId }) => api.clients.remove.mutation({ params: { id: clientId } }),
    invalidateKeys: [clientKeys.lists()],
    messages: {
      success: t('toast:clients.deleted'),
      conflict: t('toast:clients.conflict'),
      forbidden: t('toast:clients.forbidden'),
      validation: t('toast:clients.validation'),
      error: t('toast:clients.error'),
      // A kaszkád fail-closed, és a platform FOJTÁSA is bukásként jön vissza — az viszont ÁTMENETI, tehát a
      // teendő az újrapróbálás, nem a mezők ellenőrzése (a szerver erre 503-at ad).
      unavailable: t('toast:clients.unavailable'),
    },
  });

  function onSubmit(values: ClientFormValues): void {
    updateMutation.mutate(values);
  }

  function confirmDelete(): void {
    removeMutation.mutate(
      { id },
      {
        // onSuccess csak sikeren fut (a hook a 4xx-et dobja) → nincs szükség státusz-ellenőrzésre.
        onSuccess: () => {
          setConfirmOpen(false);
          void queryClient.removeQueries({ queryKey: detailKey });
          void navigate({ to: '/clients' });
        },
      },
    );
  }

  if (detailQuery.isLoading) {
    return (
      <AppShell>
        <FormPage>
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-64 w-full" />
          </div>
        </FormPage>
      </AppShell>
    );
  }

  if (!detail) {
    return (
      <AppShell>
        <FormPage>
          <EmptyState
            title={t('clients.notFound')}
            description={t('clients.notFoundDesc')}
            action={
              <Button variant="outline" onClick={() => void navigate({ to: '/clients' })}>
                {t('clients.back')}
              </Button>
            }
          />
        </FormPage>
      </AppShell>
    );
  }

  return (
    // A cím a TOPBAR morzsájában (mint a létrehozás-oldalon és a profilon) — az oldal törzsében nincs h1.
    <AppShell
      parent={{ label: t('nav.clients'), to: '/clients' }}
      title={detail.name}
      // ALCÍM NINCS: form-/detail-oldalon a morzsa („Ügyfelek / <név>") már megmondja, hol vagy és mi ez —
      // egy leíró mondat mellé csak zajt tenne. Az alcím a LISTA-oldalak sajátja (design.md).
    >
      <FormPage
        // RAGADÓS SZEKCIÓ-NAVIGÁCIÓ az űrlap melletti üres sávban (lg felett). A tartalom EGY lapon marad —
        // a rail csak ugrást ad görgetés helyett. Miért nem tab: lásd `SectionNav` fejléc-komment.
        aside={<SectionNav items={navItems} ariaLabel={t('clients.sectionNavLabel')} />}
      >
        <div className="space-y-8">
          <ClientForm
            form={form}
            mode="edit"
            disabled={!canManage}
            pending={updateMutation.isPending}
            onSubmit={onSubmit}
            t={t}
            autoBillingBlockedReason={autoBillingBlocked}
            // INAKTIVÁLÁS: mi lesz a nyitott számlázás-sorokkal. Csak akkor látszik, ha a felhasználó
            // épp az `inactive` pirulát választotta — addig zaj lenne.
            statusNotice={
              form.watch('status') === 'inactive' && detail.status !== 'inactive' ? (
                <DeactivationBillingNotice rows={assignmentsForNotice} t={t} />
              ) : null
            }
          />

          {/* A NÉGY KAPCSOLÓDÓ SZEKCIÓ — PONTOSAN abban a sorrendben, ahogy a LÉTREHOZÁS-oldalon zárolva
              megjelennek (`clients.new.tsx` → PENDING_SECTIONS): Fiókok · Szolgáltatások · Számlázási
              profilok · Bankkártyák. A sorrend-egyezés nem kozmetika: a felhasználó létrehozáskor tanulja
              meg, mi tartozik egy ügyfélhez — ha a szerkesztő-oldalon más sorrendben (vagy hiányosan) állnak,
              minden alkalommal újra kell keresnie. Üresen is megjelennek: a „nincs még" is információ.
              Mindegyik page-gate-elt a saját `*.view` jogára (a BFF is 403-at ad). */}
          {canViewAccounts && <ClientAccountsSection clientId={id} />}
          {canViewServices && <ClientServicesSection clientId={id} clientName={detail.name} />}
          {canViewPaymentCards && (
            <ClientPaymentCardsSection clientId={id} clientName={detail.name} />
          )}
          {canViewBillingProfiles && (
            <ClientBillingProfilesSection clientId={id} clientName={detail.name} />
          )}

          {/* VESZÉLYZÓNA — a lap legalján, keretes/veszély-tónusú dobozban. Korábban egy közönséges
              `FormSection` volt `outline` gombbal, ami beleolvadt a többi szekcióba; a visszafordíthatatlan
              művelet viszont épp attól veszélyes, hogy ránézésre ugyanolyan, mint a többi. */}
          {canDelete && (
            <DangerZone
              id={CLIENT_SECTION.danger}
              title={t('common.dangerZone')}
              actionTitle={t('clients.dangerTitle')}
              description={t('clients.dangerDesc')}
              actionLabel={t('clients.delete')}
              onAction={() => setConfirmOpen(true)}
            />
          )}
        </div>
      </FormPage>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('clients.deleteTitle')}
        description={t('clients.deleteDesc', { name: detail.name })}
        confirmLabel={t('clients.delete')}
        cancelLabel={t('clients.cancel')}
        pendingLabel={t('clients.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
